import { useTranslation } from 'react-i18next';
import { memberAvatarControllerServeV1, type MemberAvatarResponse } from '@kidshome/api-contract';
import { cn } from '@kidshome/ui';
import { avatarGlyph } from '@/shared/ui/glyphs';
import { presetAvatar } from '@/shared/ui/avatar-key';
import { avatarArt } from '@/assets/icons';
import { AuthImage } from '@/shared/ui/AuthImage';

// Module-level (not inline in render): AuthImage's effect depends on this function's identity, so a
// fresh closure per render would refetch/reset-to-loading on every parent re-render.
const fetchMemberAvatar = (key: string, signal?: AbortSignal): Promise<Blob> =>
  memberAvatarControllerServeV1(key, undefined, signal);

const DIMS: Record<NonNullable<AvatarProps['size']>, string> = {
  xl: 'size-20 text-4xl',
  lg: 'size-12 text-2xl',
  md: 'size-11 text-xl',
  sm: 'size-9 text-lg',
  // `xs` is a non-tappable inline marker (e.g. a "just for {child}" chip), so it's below the 44px touch
  // target — only use it where the avatar is decorative beside text, never as the sole tap affordance.
  xs: 'size-5 text-[0.6rem]',
};

interface AvatarProps {
  /**
   * The member's tagged avatar (ADR-0037): a `preset` art key OR an `uploaded` photo `imageKey`. A
   * bare preset key string is also accepted (lifted via {@link presetAvatar}) for preset-only surfaces.
   */
  avatar: MemberAvatarResponse | string | null | undefined;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Member avatar — a soft token-tinted circle. A `preset` avatar shows the chosen animal glyph; an
 * `uploaded` avatar shows the parent-curated photo, fetched through the auth-gated, BOLA-checked image
 * route (`GET /v1/members/avatar/:key` via {@link AuthImage}), round + `object-cover`, with a graceful
 * preset-glyph fallback while loading / on error. The avatar is decorative (the name is always rendered
 * beside it), so the glyph is `aria-hidden`. Sizes map to the kid-shell 44px+ touch target on tappable
 * surfaces.
 */
export function Avatar({ avatar, size = 'md', className }: AvatarProps): React.ReactElement {
  const { t } = useTranslation('common');
  const dims = DIMS[size];
  const tagged: MemberAvatarResponse =
    typeof avatar === 'string' || avatar == null ? presetAvatar(avatar) : avatar;

  const base = cn(
    'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-pill bg-surface-muted shadow-sm',
    dims,
    className,
  );

  if (tagged.kind === 'uploaded' && tagged.imageKey) {
    return (
      <span className={base}>
        <AuthImage
          imageKey={tagged.imageKey}
          fetcher={fetchMemberAvatar}
          alt={t('avatar.photoAlt')}
          fallbackLabel={t('avatar.photoAlt')}
          fallbackGlyph={avatarGlyph(null)}
          className="size-full object-cover"
        />
      </span>
    );
  }

  const art = avatarArt(tagged.key);
  return (
    <span className={base}>
      {art ? (
        <img src={art} alt="" aria-hidden="true" className="size-full object-cover" />
      ) : (
        <span aria-hidden="true">{avatarGlyph(tagged.key)}</span>
      )}
    </span>
  );
}
